import { useMemo } from "react";

export const RELATIVE_PERIODS: { value: string; label: string; group: string }[] = [
  { value: "TODAY", label: "Today", group: "Day" },
  { value: "YESTERDAY", label: "Yesterday", group: "Day" },
  { value: "LAST_7_DAYS", label: "Last 7 days", group: "Day" },
  { value: "LAST_14_DAYS", label: "Last 14 days", group: "Day" },
  { value: "LAST_30_DAYS", label: "Last 30 days", group: "Day" },
  { value: "LAST_60_DAYS", label: "Last 60 days", group: "Day" },
  { value: "LAST_90_DAYS", label: "Last 90 days", group: "Day" },
  { value: "LAST_180_DAYS", label: "Last 180 days", group: "Day" },
  { value: "THIS_WEEK", label: "This week", group: "Week" },
  { value: "LAST_WEEK", label: "Last week", group: "Week" },
  { value: "LAST_4_WEEKS", label: "Last 4 weeks", group: "Week" },
  { value: "LAST_12_WEEKS", label: "Last 12 weeks", group: "Week" },
  { value: "LAST_52_WEEKS", label: "Last 52 weeks", group: "Week" },
  { value: "THIS_MONTH", label: "This month", group: "Month" },
  { value: "LAST_MONTH", label: "Last month", group: "Month" },
  { value: "LAST_3_MONTHS", label: "Last 3 months", group: "Month" },
  { value: "LAST_6_MONTHS", label: "Last 6 months", group: "Month" },
  { value: "LAST_12_MONTHS", label: "Last 12 months", group: "Month" },
  { value: "THIS_BIMONTH", label: "This bi-month", group: "Bi-month" },
  { value: "LAST_BIMONTH", label: "Last bi-month", group: "Bi-month" },
  { value: "LAST_6_BIMONTHS", label: "Last 6 bi-months", group: "Bi-month" },
  { value: "THIS_QUARTER", label: "This quarter", group: "Quarter" },
  { value: "LAST_QUARTER", label: "Last quarter", group: "Quarter" },
  { value: "LAST_4_QUARTERS", label: "Last 4 quarters", group: "Quarter" },
  { value: "THIS_SIX_MONTH", label: "This six-month", group: "Six-month" },
  { value: "LAST_SIX_MONTH", label: "Last six-month", group: "Six-month" },
  { value: "LAST_2_SIXMONTHS", label: "Last 2 six-months", group: "Six-month" },
  { value: "THIS_FINANCIAL_YEAR", label: "This financial year", group: "Financial year" },
  { value: "LAST_FINANCIAL_YEAR", label: "Last financial year", group: "Financial year" },
  { value: "LAST_5_FINANCIAL_YEARS", label: "Last 5 financial years", group: "Financial year" },
  { value: "THIS_YEAR", label: "This year", group: "Year" },
  { value: "LAST_YEAR", label: "Last year", group: "Year" },
  { value: "LAST_5_YEARS", label: "Last 5 years", group: "Year" },
  { value: "LAST_10_YEARS", label: "Last 10 years", group: "Year" }
];

const ABSOLUTE_PATTERN = /^\d{4}(?:-?(?:0[1-9]|1[0-2])(?:-?(?:0[1-9]|[12]\d|3[01]))?|Q[1-4]|S[12]|W(?:0[1-9]|[1-4]\d|5[0-3]))?$/;

function isRelative(value: string): boolean {
  return RELATIVE_PERIODS.some((p) => p.value === value);
}

export function isValidPeriod(value: string): boolean {
  if (!value) return false;
  return isRelative(value) || ABSOLUTE_PATTERN.test(value);
}

export interface PeriodPickerProps {
  value: string;
  onChange: (next: string) => void;
  id?: string;
  disabled?: boolean;
  required?: boolean;
}

export default function PeriodPicker({ value, onChange, id, disabled, required }: PeriodPickerProps) {
  const groups = useMemo(() => {
    const m = new Map<string, typeof RELATIVE_PERIODS>();
    for (const p of RELATIVE_PERIODS) {
      const arr = m.get(p.group) ?? [];
      arr.push(p);
      m.set(p.group, arr);
    }
    return [...m.entries()];
  }, []);

  const isAbs = value && !isRelative(value);

  return (
    <div className="period-picker" style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
      <select
        id={id}
        value={isAbs ? "__absolute__" : value}
        onChange={(e) => {
          if (e.target.value === "__absolute__") onChange("");
          else onChange(e.target.value);
        }}
        disabled={disabled}
        required={required}
        style={{ flex: "1 1 auto" }}
      >
        <option value="">— Select period —</option>
        {groups.map(([group, items]) => (
          <optgroup key={group} label={`Relative · ${group}`}>
            {items.map((p) => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </optgroup>
        ))}
        <option value="__absolute__">Absolute period…</option>
      </select>
      {isAbs && (
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. 202604, 2026Q2, 2026W15"
          disabled={disabled}
          required={required}
          style={{ flex: "0 1 220px" }}
        />
      )}
    </div>
  );
}
